/**
 * Google Analytics 4 helper for a client-side React Router SPA.
 *
 * The base `gtag.js` snippet in `public/index.html` initialises GA with
 * `send_page_view: false` so we can fire one `page_view` per route change
 * ourselves (React Router doesn't reload the document, so GA's automatic
 * page_view would only fire once on the initial load).
 *
 * Usage:
 *   import { useAnalyticsPageview } from "@/lib/analytics";
 *   useAnalyticsPageview();   // inside any component rendered under <BrowserRouter>
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const GA_ID = process.env.REACT_APP_GA_ID;

/**
 * Low-level `gtag` wrapper — safely no-ops if the GA snippet failed to load
 * (e.g. dev environment without a Measurement ID, or ad-blockers).
 */
export function gtagEvent(action, params = {}) {
  if (typeof window === "undefined") return;
  if (typeof window.gtag !== "function") return;
  window.gtag("event", action, params);
}

/**
 * Fires a GA4 `page_view` on every React Router route change.
 * Call once, at the root of the app (inside <BrowserRouter>).
 */
export function useAnalyticsPageview() {
  const { pathname, search } = useLocation();

  useEffect(() => {
    if (!GA_ID) return;                       // GA disabled (no measurement ID)
    if (typeof window.gtag !== "function") return;

    window.gtag("event", "page_view", {
      page_path: pathname + search,
      page_location: window.location.href,
      page_title: document.title,
      send_to: GA_ID,
    });
  }, [pathname, search]);
}
